import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import type { BaseQLMiddleware } from '@adornis/baseql/metadata/types.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { getCurrentUserServer, loadRawEntityDataByID } from '@adornis/bh-util/util.js';
import { MeetingLink } from '@adornis/bh-video-conferencing/entities/MeetingLink.js';
import { Dependencies } from '@adornis/materialized-views/decorators/dependency.js';
import { validate } from '@adornis/validation/decorators.js';
import { and } from '@adornis/validation/functions/and.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { TimeSlot } from '../datetime/time-slot.js';
import { ParticipationState } from '../enums/enums.js';
import { mongo } from '../server/mongo.js';
import { isDefined } from '../util.js';
import { appointmentEndTimeValidation, appointmentStartTimeValidation } from './appointment.js';
import { ParticipationEinzelberatung } from './ParticipationEinzelberatung.js';
import { Product } from './products.js';
import { User } from './user.js';
import { readEntitiesByIDs } from '../server/entity-reads.js';

const counselingSlotConfigMiddleware: BaseQLMiddleware = async ({ src, context, next }) => {
  const slotConfig = await next();
  const { counselingMeetingLinkViewer, stripCounselingMeetingLinks } = await import('../server/meeting-link-access.js');
  const viewer = await counselingMeetingLinkViewer(context, src as Einzelberatung);
  return viewer.all ? slotConfig : stripCounselingMeetingLinks(slotConfig, viewer.ownBeraterID);
};

/**
 * these config-values are used to generate available slots
 * and should therefore not be used for any other application logic
 * besides generating slots
 */
@Entity()
export class EinzelberatungSlotConfiguration extends AdornisEntity {
  static override _class = 'EinzelberatungSlotConfiguration';

  @validate(and(nonOptional(), appointmentStartTimeValidation))
  @Field(type => Date)
  start!: Date;

  @validate(and(nonOptional(), appointmentEndTimeValidation))
  @Field(type => Date)
  end!: Date;

  @validate(nonOptional({ allowFalsy: true }))
  @Field(type => Boolean, { default: v => v ?? false })
  hasLunchBreak!: boolean;

  @validate(appointmentStartTimeValidation)
  @Field(type => Date)
  startLunch!: Date;

  @validate(appointmentEndTimeValidation)
  @Field(type => Date)
  endLunch!: Date;

  @Field(type => Int)
  slotDuration: Maybe<number>;

  @Field(type => [String], { default: v => v ?? [] })
  beraterIDs!: string[];

  @Field(type => [EinzelberatungBeraterMeetingLink], { default: v => v ?? [] })
  beraterMeetingLinks!: EinzelberatungBeraterMeetingLink[];
}

@Entity()
export class EinzelberatungBeraterMeetingLink extends AdornisEntity {
  static override _class = 'EinzelberatungBeraterMeetingLink';

  // beraterID is unique per Einzelberatung — use it as the merge key
  // so baseql can diff/merge the array on save.
  static override resolveID(entity: EinzelberatungBeraterMeetingLink): string {
    return entity.beraterID;
  }

  @validate(nonOptional())
  @Field(type => String)
  beraterID!: string;

  @Field(type => MeetingLink)
  meetingLink: Maybe<MeetingLink>;
}

@Entity()
export class Einzelberatung extends Product {
  static override _class = 'Einzelberatung' as const;

  @Field(type => EinzelberatungSlotConfiguration, { middlewares: [counselingSlotConfigMiddleware] })
  slotConfig: Maybe<EinzelberatungSlotConfiguration>;

  @Field(type => [EinzelberatungSlot], { default: v => v ?? [] })
  slots!: EinzelberatungSlot[];

  @DataLoadedField(type => [ParticipationEinzelberatung], einzelberatung => ({ productID: einzelberatung._id }))
  participations!: ParticipationEinzelberatung[];

  @Dependencies({
    classes: () => [Einzelberatung],
    ids: function (this: PartialEntityData<Einzelberatung>) {
      return [this._id!];
    },
  })
  @Field(type => Date, {
    resolve: function (this: Einzelberatung) {
      return async () => {
        if (this.startDate !== null && this.startDate !== undefined) return this.startDate;
        const fields = { slotConfig: { start: 1 } } as const;
        const [einzelberatung] = await readEntitiesByIDs<typeof Einzelberatung, typeof fields>(
          Einzelberatung,
          [this._id],
          fields,
        );
        return einzelberatung?.slotConfig?.start;
      };
    },
  })
  startDate: Maybe<Date>;
}

@Entity()
export class EinzelberatungSlot extends TimeSlot {
  static override _class = 'BeraterAvailabilities';

  // this should be a userID
  @validate(nonOptional())
  @Field(type => String)
  beraterID: Maybe<string>;

  @Field(type => User, {
    resolve(this: EinzelberatungSlot) {
      return async () => {
        const user = await getCurrentUserServer();
        if (!user) return null;
        if (!user.isAdmin && !user.isSuperAdmin) return null;
        return loadRawEntityDataByID(User, this.beraterID as string) as Promise<User>;
      };
    },
  })
  berater: Maybe<User>;

  @Dependencies({
    classes: () => [ParticipationEinzelberatung],
    ids: async function (this: PartialEntityData<EinzelberatungSlot>) {
      const c = mongo().collection(ParticipationEinzelberatung._collectionName);
      const participations = (await c.find({ timeslotID: this._id }).toArray()).filter(
        p => p.state === ParticipationState.in,
      );
      return participations.map(p => p._id as any as string).filter(isDefined);
    },
  })
  @Field(type => Boolean, {
    resolve(this: EinzelberatungSlot) {
      return async () => {
        const c = mongo().collection(ParticipationEinzelberatung._collectionName);
        const participations = (await c.find({ timeslotID: this._id }).toArray()).filter(
          p => p.state === ParticipationState.in,
        );
        return participations.length > 0; // isBooked when participation exists
      };
    },
  })
  isBooked!: boolean;
}
